import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/router"
import { BlogCard, BlogSkeletonLoader, Pagination } from "../../components/blogs"
import { FooterSection, HeaderSection } from "../../components/layout"
import { AuthorPageLoader } from "../../components/loaders"
import { Loader } from "../../components/common"
import styles from "../../styles/pages/AuthorPage.module.css"
import { fetchAuthorBySlug, fetchConfigPage } from "../../utils/api"
import { replaceImageUrl } from "../../utils/helpers/imageUtils"
import SEOHead, { getAuthorSEO } from "../../utils/helpers/seo"
import { useAuthorData } from "../../utils/hooks/useAuthorData"
import { useDarkMode } from "../../utils/hooks/useDarkMode"



export default function AuthorPage({ initialData, error }) {
  const router = useRouter()
  const { authorData, author, blogs, pagination, isLoading, currentPage, handlePageChange } = useAuthorData(initialData)

  // Dark mode hook
  const { isDarkMode, isThemeInitialized } = useDarkMode()

  // Show loading state for initial page load
  if (!initialData) {
    return <Loader type="bar" message="Loading..." fullPage={true} />;
  }

  // Show error state
  if (error || !author) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <h1 className={styles.errorTitle}>Author Not Found</h1>
          <p className={styles.errorMessage}>The author you&apos;re looking for doesn&apos;t exist.</p>
          <Link href="/blogs" className={styles.backLink}>
            ← Back to Blogs
          </Link>
        </div>
      </div>
    )
  }

  const configData = initialData?.configData || {}
  const header = configData.header || {}
  const footer = configData.footer || {}
  const pageData = initialData?.authorData?.data || {}


  // Generate SEO data for this author page
  const seoData = getAuthorSEO(author, configData, `/author/${router.query.slug}`);


  return (
    <>

      <SEOHead 
        pageData={author}
        configData={configData}
        pageUrl={`/author/${router.query.slug}`}
        customSEO={{
          title: author?.seo?.metaTitle || seoData.title,
          description: author?.seo?.metaDescription || seoData.description,
          canonical: author?.seo?.canonicalURL || seoData.canonical,
          openGraph: {
            title: author?.seo?.metaSocial?.[0]?.title || author?.seo?.metaTitle || seoData.title,
            description: author?.seo?.metaSocial?.[0]?.description || author?.seo?.metaDescription || seoData.description,
            image: author?.seo?.metaImage?.url || author?.image?.url || seoData.openGraph.images[0]?.url,
            type: 'profile',
            url: author?.seo?.canonicalURL || seoData.canonical,
          },
          twitter: {
            title: author?.seo?.metaSocial?.[1]?.title || author?.seo?.metaTitle || seoData.title,
            description: author?.seo?.metaSocial?.[1]?.description || author?.seo?.metaDescription || seoData.description,
            image: author?.seo?.metaImage?.url || author?.image?.url || seoData.twitter.images,
          },
          structuredData: author?.seo?.structuredData || seoData.structuredData,
        }}
      />

      <HeaderSection data={header} pageUrl={`/author/${router.query.slug}`} phoneNumber={pageData?.EnhancedHeroSection?.phone} />

      <main className={styles.mainContent}>
        <div className="tw-container">
          <div className={styles.authorLayout}>
            {/* Left Sidebar - Author Profile */}
            <aside className={styles.authorSidebar}>
              <div className={styles.authorProfile}>
                {/* Author Image */}
                <div className={styles.authorImageContainer}>
                  {author?.image?.url ? (
                    <Image
                      src={replaceImageUrl(author.image.url)}
                      alt={author.image.alternativeText || author.name}
                      width={200}
                      height={200}
                      className={styles.authorImage}
                    />
                  ) : (
                    <div className={styles.placeholderImage}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Author Name */}
                <h1 className={styles.authorName}>{author?.name}</h1>

                {/* Expertise */}
                {author?.expertises && author.expertises.length > 0 && (
                  <div className={styles.expertiseSection}>
                    <h3 className={styles.sectionTitle}>Expertise</h3>
                    <div className={styles.expertiseTags}>
                      {author.expertises.map((expertise, index) => (
                        <span key={index} className={styles.expertiseTag}>
                          {expertise}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Social Links */}
                {author?.social && (author.social.linkedin || author.social.instagram) && (
                  <div className={styles.socialSection}>
                    <h3 className={styles.sectionTitle}>Connect with me</h3>
                    <div className={styles.socialLinks}>
                      {author.social.linkedin && (
                        <a
                          href={author.social.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.socialLink}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                          </svg>
                          LinkedIn
                        </a>
                      )}
                      {author.social.instagram && (
                        <a
                          href={author.social.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.socialLink}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                          </svg>
                          Instagram
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </aside>

            {/* Main Content Area */}
            <main className={styles.mainContentArea}>
              {/* About Section */}
              {author?.about && (
                <section className={styles.aboutSection}>
                  <h2 className={styles.sectionTitle}>About</h2>
                  <div className={styles.aboutContent}>
                    {author.about.split('\n').map((paragraph, index) => (
                      <p key={index} className={styles.aboutText}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              )}

              {/* Articles Section */}
              <section className={styles.articlesSection}>
                <h2 className={styles.sectionTitle}>Articles by {author?.name}</h2>
                
                {isLoading ? (
                  <BlogSkeletonLoader count={9} />
                ) : blogs.length === 0 ? (
                  <div className={styles.noArticlesMessage}>
                    <div className={styles.noArticlesIcon}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                      </svg>
                    </div>
                    <h3>No Articles Found</h3>
                    <p>This author hasn&apos;t published any articles yet.</p>
                  </div>
                ) : (
                  <>
                    <div className={styles.articlesGrid}>
                      {blogs.map((blog) => (
                        <BlogCard key={blog.id} blog={blog} hideAuthor={true} />
                      ))}
                    </div>

                    {/* Pagination */}
                    {pagination.pageCount > 1 && (
                      <Pagination
                        currentPage={pagination.page}
                        totalPages={pagination.pageCount}
                        onPageChange={handlePageChange}
                        queryParams={{}}
                      />
                    )}
                  </>
                )}
              </section>
            </main>
          </div>
        </div>
      </main>

      <FooterSection data={footer} />
    </>
  )
}



// Server-side data fetching
export async function getServerSideProps(context) {
  try {
    const { slug, page } = context.query
    const currentPage = page ? Number.parseInt(page, 10) : 1

    if (!slug) {
      return {
        notFound: true,
      }
    }

    // Fetch all required data in parallel
    // const [configData] = await Promise.all([
    const [configData, authorData] = await Promise.all([
      fetchConfigPage(),
      fetchAuthorBySlug(slug, currentPage, 9)
    ])

    // For testing purposes, use test data for any slug
    if (!configData || !authorData) {
      return {
        notFound: true,
      }
    }



    return {
      props: {
        initialData: {
          configData: configData,
          authorData: authorData,
        },
        error: null,
      },
    }
  } catch (error) {
    console.error('Error fetching author page data:', error)
    return {
      props: {
        initialData: null,
        error: error.message,
      },
    }
  }
}
